import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CardGiftcardOutlinedIcon from '@mui/icons-material/CardGiftcardOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import {IconButton, Switch} from '@mui/material';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';

import type {Router} from '@toolpad/core/AppProvider';
import React, {useState} from 'react';
import {
  Column,
  SimpleTable
} from '../../../components/SimpleTable';
import {LicenseType} from '../../../model/license/license-type';
import {OrganizationData} from '../../../model/organization/organization-data';
import {FormatUtils} from '../../../utils/format.utils';
import {AssignPackageDrawer} from './components/AssignPackageDrawer';
import {CreateOrganizationDrawer} from './components/CreateOrganizationDrawer';
import {CreateOrganizationLicenseDrawer} from './components/CreateOrganizationLicenseDrawer';
import {useOrganizationListPageState} from './state/organization-list-page-state';
import HeadsetMicOutlinedIcon from '@mui/icons-material/HeadsetMicOutlined';

type  Props = {
  router: Router
}

const OrganizationListPage = ({router}: Props) => {
  const state = useOrganizationListPageState();
  const [assignPackageOrg, setAssignPackageOrg] = useState<OrganizationData | null>(null)

  const buildEmptyLicense = (organizationId: string) =>  <>
    <IconButton onClick={() => state.setCreateOrganizationLicenseDrawerOpen(true, organizationId)} aria-label={state.translate('common.details.title')}>
      <AddCircleOutlineIcon />
    </IconButton>
    {state.translate('organizations-page.emptyLicense.title')}
  </>

  const buildExistingLicense = (organizationId: string, organizationName: string, licenseType: LicenseType) => <>
    <IconButton onClick={() => setAssignPackageOrg({id: organizationId, name: organizationName} as OrganizationData)} aria-label={state.translate('organizations-page.assignPackage.title')} title={state.translate('organizations-page.assignPackage.title')}>
      <CardGiftcardOutlinedIcon/>
    </IconButton>
    <IconButton onClick={() => router.navigate(`/support-plan/${organizationId}`)} aria-label={state.translate('common.details.title')}>
      <HeadsetMicOutlinedIcon />
    </IconButton>
    <IconButton onClick={() => router.navigate(`/organization-users/${organizationId}`)} aria-label={state.translate('common.details.title')}>
      <PeopleOutlinedIcon />
    </IconButton>
    <IconButton onClick={() => router.navigate(`/organization-details/${organizationId}`)} aria-label={state.translate('common.details.title')}>
      <DescriptionOutlinedIcon />
    </IconButton>
    {state.translate(`licenseType.${licenseType}`)}
  </>

  const columns: Column<OrganizationData>[] = [
    { key: 'id', minWidth: 200, label: state.translateColumn('id'), render: (row) => row.id },
    { key: 'name', minWidth: 200, label: state.translateColumn('name'), render: (row) => row.name },
    { key: 'createDate', minWidth: 200, label: state.translateColumn('createDate'), render: (row) => FormatUtils.formatDate(row.createDate) },
    { key: 'customerMonthlyCost', minWidth: 100, label: state.translateColumn('customerMonthlyCost'), render: (row) => {
        const existingLicense = state.getOrganizationLicense(row.id)

        return existingLicense === null
          ? <></>
          : FormatUtils.formatCurrency(existingLicense.customerMonthlyCost)
      }
    },
    { key: 'discount', minWidth: 100, label: state.translateColumn('discount'), render: (row) =>  {
        const existingLicense = state.getOrganizationLicense(row.id)

        return existingLicense === null
          ? <></>
          : `${existingLicense?.discount?.toFixed(2) ?? 0}%`
      }
    },
    { key: 'licenseType', minWidth: 50, label: state.translateColumn('licenseType'), render: (row) => {
        const existingLicense = state.getOrganizationLicense(row.id)

        return existingLicense === null
          ? buildEmptyLicense(row.id)
          : buildExistingLicense(row.id, row.name, existingLicense.licenseType)
      },
    },
    { key: 'active', minWidth: 50, label: state.translateColumn('active'), render: (row) => {
       return <Switch
          checked={row.active}
          onChange={async (event) => await state.toggleOrganizationActive(row.id, event.target.checked)}
          color="primary"
          inputProps={{ 'aria-label': 'toggle active' }}
        />
      },
    },
  ]

  const buildCreateOrganizationButton = () => <Box display="flex" justifyContent="flex-end" mb={2}>
    <Button
      variant="contained"
      color="primary"
      onClick={() => state.setDrawerOpen(true)}
    >
      Dodaj nowe wdrożenie
    </Button>
  </Box>


  return (
    <Paper sx={{height: '100%', width: '100%', padding: 2, position: 'relative'}}>
      {buildCreateOrganizationButton()}
      <SimpleTable
        rows={[...state.organizations].sort((a, b) => a.name.localeCompare(b.name))}
        columns={columns}
        rowKey={(row) => row.id}
      />
      <CreateOrganizationDrawer
        isDrawerOpen={state.drawerOpen}
        createOrganization={state.createOrganization}
        onDrawerClosed={() => state.setDrawerOpen(false)}
      />
      <CreateOrganizationLicenseDrawer
        createOrganizationLicense={state.createOrganizationLicense}
        isDrawerOpen={state.createOrganizationLicenseDrawerOpen}
        onDrawerClosed={() => state.setCreateOrganizationLicenseDrawerOpen(false, null)}
      />
      <AssignPackageDrawer
        isOpen={assignPackageOrg !== null}
        onClose={() => setAssignPackageOrg(null)}
        organizationId={assignPackageOrg?.id ?? null}
        organizationName={assignPackageOrg?.name ?? null}
      />
    </Paper>
  );
};

export default OrganizationListPage;
